import { forwardRef, type ComponentPropsWithoutRef } from "react";
import {
  VoiceToTextTextarea as _VTTTextarea,
  VoiceToTextInput as _VTTInput,
  VoiceToTextButton as _VTTButton,
} from "@/components/voice-to-text-button";

// ---------------------------------------------------------------------------
// Thin indirection wrappers for the voice-to-text fields.
//
// Why this file exists: the Invoices page (~4k lines) is the largest module in
// the statically-imported entry chunk, and Rollup's scope-hoisting intermittently
// failed to wire its direct reference to the shared `VoiceToTextTextarea` export,
// throwing `VoiceToTextTextarea is not defined` at render time and crashing the
// whole page. ~87 smaller modules import the same component without issue.
//
// Routing the heavy page's voice imports through these small re-defined wrappers
// means the page references a symbol declared in a normal-sized module (like every
// other working importer) instead of the binding that fails to hoist. Graceful
// degradation lives inside the shared component itself, so these stay thin.
//
// ALL consumer files import from this module (not voice-to-text-button directly)
// so this indirection applies uniformly across the entire bundle. Do not import
// VoiceToTextTextarea / VoiceToTextInput / VoiceToTextButton directly from
// voice-to-text-button anywhere except this file.
// ---------------------------------------------------------------------------

export const VoiceTextarea = forwardRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<typeof _VTTTextarea>
>((props, ref) => <_VTTTextarea ref={ref} {...props} />);
VoiceTextarea.displayName = "VoiceTextarea";

export const VoiceInput = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<typeof _VTTInput>
>((props, ref) => <_VTTInput ref={ref} {...props} />);
VoiceInput.displayName = "VoiceInput";

export function VoiceButton(
  props: ComponentPropsWithoutRef<typeof _VTTButton>,
) {
  return <_VTTButton {...props} />;
}

// Compatibility aliases — keep the original names working so consumer files
// only need their import path changed, not their usage sites.
export const VoiceToTextTextarea = VoiceTextarea;
export const VoiceToTextInput = VoiceInput;
export const VoiceToTextButton = VoiceButton;
